/**
 * Client-side mirror of API `buildNexReturnSlipPutRequests` for submit confirmation UI.
 * Keep in sync with partbay-api/.../buildNexReturnSlipRequests.ts
 */
import type { InvoiceDetailResponse } from '../types/invoice';
import { resolveNexFolderNoForPoHeader } from './nexFolderResolution';

export interface ReturnSlipPutPreviewRow {
    folderNo: number;
    poHeaderID: number;
    poLines: number[];
}

export type ReturnSlipSubmitPreviewResult =
    | { ok: true; requests: ReturnSlipPutPreviewRow[] }
    | { ok: false; reason: string };

function toUnmatchedSet(raw: Set<number> | undefined): Set<number> {
    return raw ?? new Set();
}

export function isIgnoredRowMeaningful(part: { partNumber?: string; title?: string } | undefined): boolean {
    if (!part) return false;
    const pn = String(part.partNumber ?? '').trim();
    const t = String(part.title ?? '').trim();
    return pn.length > 0 || t.length > 0;
}

export function ignoredRowGlobalPartIndex(args: {
    ignoredIndex: number;
    lineItemsLength: number;
    freightItemsLength: number;
    fuelItemsLength: number;
    restockingItemsLength: number;
    discountItemsLength: number;
}): number {
    return (
        args.lineItemsLength +
        args.freightItemsLength +
        args.fuelItemsLength +
        args.restockingItemsLength +
        args.discountItemsLength +
        args.ignoredIndex
    );
}

export function computeReturnSlipSubmitPreview(args: {
    invoice: InvoiceDetailResponse;
    editedFields: Partial<InvoiceDetailResponse>;
    unmatchedPartIndices: Set<number>;
    localMatchedNexOverrides: Record<number, unknown>;
}): ReturnSlipSubmitPreviewResult {
    const { invoice, editedFields, unmatchedPartIndices, localMatchedNexOverrides } = args;
    const unmatched = toUnmatchedSet(unmatchedPartIndices);

    const lineItems = editedFields.lineItems ?? invoice.lineItems ?? [];
    const freightItems = editedFields.freightItems ?? invoice.freightItems ?? [];
    const fuelItems = editedFields.fuelItems ?? invoice.fuelItems ?? [];
    const restockingItems = editedFields.restockingFeeItems ?? invoice.restockingFeeItems ?? [];
    const discountItems = editedFields.discountItems ?? invoice.discountItems ?? [];
    const ignoredList =
        editedFields.ignoredParts ??
        invoice.ignoredParts ??
        (invoice as { canceledParts?: typeof invoice.ignoredParts }).canceledParts ??
        [];

    const L = lineItems.length;
    const F = freightItems.length;
    const Fu = fuelItems.length;
    const R = restockingItems.length;
    const D = discountItems.length;

    type NexPo = { folder?: number; detailLines?: Array<{ poHeaderID?: number; lineNumber?: number }> };
    const nexResponse = (invoice as { nexResponse?: unknown }).nexResponse as NexPo[] | null | undefined;

    const matchedNexLines = invoice.reconciliationIssues?.matchedNexLines ?? [];
    const matchedNexLinesForIgnored =
        (
            invoice.reconciliationIssues as {
                matchedNexLinesForIgnored?: Array<{ ignoredIndex: number; nexLine: Record<string, unknown> }>;
            } | undefined
        )?.matchedNexLinesForIgnored ?? [];

    const headerToLineNums = new Map<number, Set<number>>();

    const addLine = (hid: number, lineNo: number) => {
        if (!headerToLineNums.has(hid)) headerToLineNums.set(hid, new Set());
        headerToLineNums.get(hid)!.add(lineNo);
    };

    const pushFromNexLine = (
        nexLine: { poHeaderID?: number; lineNumber?: number },
        ctx: string
    ): ReturnSlipSubmitPreviewResult | null => {
        const hid = nexLine?.poHeaderID;
        const ln = nexLine?.lineNumber;
        if (hid === undefined || hid === null || !Number.isFinite(Number(hid))) {
            return { ok: false, reason: `Missing poHeaderID (${ctx}).` };
        }
        if (ln === undefined || ln === null || !Number.isFinite(Number(ln))) {
            return { ok: false, reason: `Missing NEX line number (${ctx}).` };
        }
        addLine(Number(hid), Number(ln));
        return null;
    };

    for (const m of matchedNexLines) {
        if (unmatched.has(m.partIndex)) continue;
        const override = localMatchedNexOverrides[m.partIndex] as { poHeaderID?: number; lineNumber?: number } | undefined;
        const nexLine = (override ?? m.nexLine) as { poHeaderID?: number; lineNumber?: number };
        const err = pushFromNexLine(nexLine, `partIndex ${m.partIndex}`);
        if (err) return err;
    }

    const matchedIgnoredByIndex = new Map<number, { poHeaderID?: number; lineNumber?: number }>();
    for (const row of matchedNexLinesForIgnored) {
        matchedIgnoredByIndex.set(row.ignoredIndex, row.nexLine as { poHeaderID?: number; lineNumber?: number });
    }

    for (let i = 0; i < ignoredList.length; i++) {
        if (!isIgnoredRowMeaningful(ignoredList[i])) continue;
        const g = ignoredRowGlobalPartIndex({
            ignoredIndex: i,
            lineItemsLength: L,
            freightItemsLength: F,
            fuelItemsLength: Fu,
            restockingItemsLength: R,
            discountItemsLength: D,
        });
        if (unmatched.has(g)) continue;

        const fromIgnored = matchedIgnoredByIndex.get(i);
        const fromManual = localMatchedNexOverrides[g] as { poHeaderID?: number; lineNumber?: number } | undefined;
        const fromMatchedLine = matchedNexLines.find((m) => m.partIndex === g)?.nexLine as
            | { poHeaderID?: number; lineNumber?: number }
            | undefined;
        const nexLine = fromManual ?? fromMatchedLine ?? fromIgnored;
        if (!nexLine) {
            return {
                ok: false,
                reason: `Match each returned item to a NEX return line (row ${i + 1}). Use the light bulb on that row to pick a line.`,
            };
        }
        const err = pushFromNexLine(nexLine, `ignored row ${i + 1}`);
        if (err) return err;
    }

    if (headerToLineNums.size === 0) {
        return {
            ok: false,
            reason: 'No NEX lines matched yet. Match ignored lines to NEX /returns, then submit.',
        };
    }

    const returnsPos = nexResponse ?? [];
    if (returnsPos.length === 0) {
        return { ok: false, reason: 'No NEX returns data for this RO.' };
    }

    const requests: ReturnSlipPutPreviewRow[] = [];

    for (const [poHeaderID, lineSet] of Array.from(headerToLineNums.entries())) {
        const folderNo = resolveNexFolderNoForPoHeader(poHeaderID, returnsPos);
        if (folderNo === null) {
            const attemptedLines = Array.from(lineSet).sort((a, b) => a - b);
            return {
                ok: false,
                reason: `Could not resolve folderNo from NEX returns for poHeaderID=${poHeaderID} (lines ${attemptedLines.join(', ')}).`,
            };
        }
        requests.push({
            folderNo,
            poHeaderID,
            poLines: Array.from(lineSet).sort((a, b) => a - b),
        });
    }

    requests.sort((a, b) => a.poHeaderID - b.poHeaderID);
    return { ok: true, requests };
}
